import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams } from 'react-router-dom';
import * as api from "../api/apiConnector.js";
import UserContext from '../components/User_Context.js';
import * as breadcrumb from "../components/ui/breadcrumb.js";
import * as menu from "../assets/menus/menu.js";
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import * as utils from "../components/utils.js";
import {
    PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
    LineChart, Line
} from 'recharts';

function InvoiceAnalyzerScreen() {
    const { id = null } = useParams();
    const { currentUser } = useContext(UserContext);
    const [document, setDocument] = useState(null);
    const [analyzing, setAnalyzing] = useState(false);
    const [answerHtml, setAnswerHtml] = useState("");
    const [history, setHistory] = useState([]);
    const [toAddInTheHistory, setToAddInTheHistory] = useState(null);
    const [chartComponent, setChartComponent] = useState(null);

    let system_message_timer = null;

    // Fake Charts for testing/demo
    const InvoicePieChart = () => {
        const data = [
            { name: 'Materia energia', value: 303.32 },
            { name: 'Trasporto e contatore', value: 72.75 },
            { name: 'Oneri di sistema', value: 133.19 },
            { name: 'IVA', value: 117.39 },
            { name: 'Imposte', value: 25.31 }
        ];
        const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7f50', '#a4de6c'];

        return (
            <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                    <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label isAnimationActive>
                        {data.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                </PieChart>
            </ResponsiveContainer>
        );
    };

    const InvoiceBarChart = () => {
        const data = [
            { name: 'Gennaio', spesa: 400 },
            { name: 'Febbraio', spesa: 300 },
            { name: 'Marzo', spesa: 500 },
            { name: 'Aprile', spesa: 200 }
        ];
        return (
            <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="spesa" fill="#8884d8" isAnimationActive />
                </BarChart>
            </ResponsiveContainer>
        );
    };

    const InvoiceLineChart = () => {
        const data = [
            { name: 'Lun', value: 120 },
            { name: 'Mar', value: 200 },
            { name: 'Mer', value: 150 },
            { name: 'Gio', value: 300 },
            { name: 'Ven', value: 250 }
        ];
        return (
            <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="value" stroke="#82ca9d" strokeWidth={2} isAnimationActive />
                </LineChart>
            </ResponsiveContainer>
        );
    };

    // Function to retrieve file info
    const getFile = async (fileId) => {
        const result = await api.sendMessage("dms/getFileInfo", {
            user: currentUser(),
            body: {
                id: fileId
            }
        });

        return result;
    };

    // Function to start AI chat
    const startChat = async () => {
        if (!window.client) {
            const _client = new window.EventSource(`${process.env.REACT_APP_API_URL}/dms/startChat`);
            _client.onmessage = (e) => {
                _parseMessage(JSON.parse(e.data));
            }

            window.client = {
                emitter: _client,
                html: ""
            }

            return _client;
        }
    }

    // Function to handle incoming messages from AI
    const _parseMessage = (message) => {
        switch (message.type) {
            case "stopping": {
                window.document.location.href = '/';
            }
                break;
            case "activity": {
                switch (message.ai_source?.action) {
                    case "file_progress": {
                        if (message.ai_source?.fileName) {
                            const _percent = (message.ai_source.parsed * 100 / message.ai_source.total).toFixed(1);

                            show_system_message({
                                message: `<h6><span data-progress-file-name="file-name"></span>
                                <span class="pull-end">${_percent}%</span>
                                </h6>
                                <div class="progress">
                                    <div class="progress-bar bg-danger" style="width: ${_percent}%;" role="progressbar">
                                        <span class="sr-only">${_percent}% Complete</span>
                                    </div>
                                </div>	
                                <div>
                                <small>${message.ai_source.fileName}</small>
                                </div>`,
                                timeOut: 0
                            });
                        }
                    }
                        break;
                    case "answer_starting": {
                        window.client.starting = true;

                        show_system_message({
                            timeOut: 0,
                            message: `
                            <div class="text-center">
                                <i class="fa fa-2x fa-hourglass-start"></i>
                            </div>`
                        });
                    }
                        break;
                    default: {
                        show_system_message({
                            message: `${message.message}`,
                            timeOut: 0
                        });
                    }
                }
            }
                break;
            case "chat": {
                if (window.client.starting) {
                    window.client.html = "";
                    window.client.starting = false;
                }

                window.client.html += message.message;
                _normalize_chat();

                setAnswerHtml(window.client.html);
                _parse_html();

                show_system_message({
                    message: `
                    <div class="text-center">
                        <i class="fa fa-2x fa-network-wired"></i>
                    </div>`,
                    timeOut: 500
                });

                if (message.done) {
                    setAnalyzing(false);
                    setToAddInTheHistory({
                        id: message.id,
                        question: message.prompt,
                        answer: message.answer
                    });

                    // Scegli un grafico casuale e salvalo nello stato
                    const chartOptions = [<InvoicePieChart key="1" />, <InvoiceBarChart key="2" />, <InvoiceLineChart key="3" />];
                    const randomChart = chartOptions[Math.floor(Math.random() * chartOptions.length)];
                    setChartComponent(randomChart);

                    window.$("#answer-actions").show();
                } else {
                    window.$("#answer-actions").hide();
                }
            }
                break;
            case "wellcome": {
                window.client.id = message.id;
                _sendMessage({
                    type: "authenticate",
                    token: currentUser().token,
                    file_id: id
                });

                show_system_message({
                    timeOut: 0,
                    message: `<strong>Connecting to AI engine</strong>...`
                });
            }
                break;
            case "authenticated": {
                show_system_message({
                    message: `<strong>Connected</strong>...`
                });

                // Inviare automaticamente il prompt per l'analisi della fattura
                analyzeInvoice();
            }
                break;
            default:
                break;
        }
    }

    // Function to normalize chat content (removes markdown wrappers)
    const _normalize_chat = () => {
        if (window.client && window.client.html) {
            if (window.client.html.startsWith("```html")) {
                window.client.html = window.client.html.substring(7);
            }

            if (window.client.html.endsWith("``` ") && window.client.html.length > 4) {
                window.client.html = window.client.html.substring(0, window.client.html.length - 4);
            }

            if (window.client.html.includes("```html") && window.client.html.endsWith("```")) {
                const startIndex = window.client.html.indexOf("```html") + 7;
                window.client.html = window.client.html.substring(startIndex, window.client.html.length);
                window.client.html = window.client.html.substring(0, window.client.html.length - 3);
            }
        }
    }

    // Function to clean HTML output and apply tweaks
    const _parse_html = () => {
        const _div = window.$("#current-answer-div");
        _div.find(".table").removeClass("table");
    }

    // Function to display chart if available
    const getChart = () => {
        return (
            <div className="chart-container mt-4 mb-4 text-center">
                <h5>Analisi Grafica</h5>
                {chartComponent}
            </div>
        );
    };

    // Function to send a message to the AI backend
    const _sendMessage = async (message) => {
        if (window.client?.id) {
            message.id = window.client.id;
            message.tutor = "AI_PLAN"; // Default tutor
        }

        var result = await api.sendMessage("dms/sendChatMessage", {
            user: currentUser(),
            body: {
                message
            }
        });
    }

    // Function to show system-level messages
    const show_system_message = (params) => {
        const { message, timeOut = 2000 } = params;

        const inner_div = window.$(`<div class="col-xl-12">
            <div class="card dz-card">
                <div class="card-header flex-wrap border-0">
                    <div>
                        <h4 class="card-title">AI Plan</h4>
                    </div>											
                </div>
                <div class="tab-content">
                    <div class="tab-pane fade active show" role="tabpanel">
                        <div class="card-body pt-0">
                            <h6><span></span>
                                <span class="pull-end"></span>
                            </h6>                                                                        
                        </div>
                    </div>
                </div>
            </div>
        </div>`);

        let div = window.$("#chat-system-message");

        if (div.length == 0) {
            div = window.$(`<div id="chat-system-message" style="position: fixed; right: 20px; bottom: 60px; width: 350px; height: 100px; opacity: 0.75"></div>`);
            window.$("body").append(div);
            div.append(inner_div);
        }

        const message_div = div.find(".pull-end");

        message_div.html("");
        message_div.append(window.$("<div/>").html(message));

        if (system_message_timer) {
            clearTimeout(system_message_timer);
        }

        if (timeOut > 0) {
            system_message_timer = setTimeout(() => {
                div.remove();
            }, timeOut);
        }
    }

    // Function to launch invoice analysis via AI
    const analyzeInvoice = async () => {
        setAnalyzing(true);
        window.$("#current-answer-div").html("");
        window.$("#chart-container").html("");

        // Prompt aggiornato per estrazione e formattazione completa della fattura
        const invoiceAnalysisPrompt = `
Analizza attentamente questa fattura ed estrai tutte le informazioni rilevanti in due sezioni ben distinte:

  1. Riepilogo Importi Fatturati (mostrato in alto)
Presenta il riepilogo degli importi in una tabella ben impaginata, con queste voci:
- Spesa per materia energia
- Spesa per trasporto e gestione contatore
- Spesa per oneri di sistema
- IVA
- Imposte
- Totale a debito

Verifica che la somma delle voci corrisponda al totale indicato. Se non combacia, segnala l'incongruenza in modo evidente.

   2. Dettagli Fattura (tabella sotto)
   Mostra una tabella con due colonne: "Campo" e "Valore", includendo sempre gli stessi campi standardizzati a prescindere dal tipo di fornitura (es. gas, luce o altro),
   anche se alcuni valori risultano vuoti.
   Se un dato non è disponibile, lascia il valore vuoto o scrivi “Non disponibile”.

Usa **questo schema fisso per i nomi delle colonne**, in questo ordine:

- Tipo di fornitura
- Numero fattura 
- Data fattura
- Fornitore - Nome
- Fornitore - Indirizzo
- Fornitore - Partita IVA
- Cliente - Intestatario
- Cliente - Indirizzo
- Cliente - Codice fiscale
- Codice cliente
- Descrizione servizio
- Quantità
- Unità di misura
- Periodo di fatturazione
- Metodo di pagamento
- Scadenza pagamento
- Sconti applicati
- Note
- Codice POD / PDR
- Spesa materia prima
- Spesa trasporto e contatore
- Oneri di sistema
- IVA
- Imposte
- Totale a debito

Tutti questi campi devono **apparire sempre**, anche se non valorizzati.

 Formattazione e layout richiesti:
- Il riepilogo va mostrato in alto, **sopra la tabella dei dettagli**.
- Tutte le sezioni devono essere ben leggibili, con intestazioni chiare.
- Usa HTML con questi stili CSS: max-width impostato a 800px, margin centrato (auto), e padding interno per rendere le celle leggibili.
- Nessun layout affiancato: tutto **impilato verticalmente**.
- Includi eventualmente un blocco <style> per migliorare la leggibilità.
 Esempio struttura HTML desiderata:

<h2>Riepilogo Importi Fatturati</h2>
<table>
  <tr><td>Spesa per materia energia:</td><td>303,32 €</td></tr>
  <tr><td>Spesa per trasporto e gestione contatore:</td><td>72,75 €</td></tr>
  <tr><td>Spesa per oneri di sistema:</td><td>133,19 €</td></tr>
  <tr><td>IVA:</td><td>117,39 €</td></tr>
  <tr><td>Imposte:</td><td>25,31 €</td></tr>
  <tr><th>Totale a debito:</th><th>650,96 €</th></tr>
</table>

<h2>Dettagli Fattura</h2>
<table>
  <tr><th>Campo</th><th>Valore</th></tr>
  <tr><td>Numero fattura</td><td>...</td></tr>
  <tr><td>Data fattura</td><td>...</td></tr>
  ...
</table>

Aggiungi anche uno stile opzionale come:

<style>
  table {
    width: 100%;
    max-width: 800px;
    margin: 20px auto;
    border-collapse: collapse;
  }
  table td, table th {
    padding: 10px;
    border: 1px solid #ddd;
    text-align: left;
  }
  h2 {
    max-width: 800px;
    margin: 40px auto 10px;
    font-size: 1.4rem;
  }
</style>

Fornisci tutto in formato HTML pulito, pronto per essere visualizzato in una pagina.
    `;

        // Mostra messaggio di attesa
        show_system_message({
            timeOut: 0,
            message: `
        <div class="text-center">
            <i class="fa fa-2x fa-hourglass-start"></i>
        </div>`
        });

        // Invia il prompt all'AI per l'elaborazione
        await _sendMessage({
            type: "ai_query",
            query: invoiceAnalysisPrompt
        });
    };

    // Action buttons for like/dislike/regenerate/share
    const ActionButtons = ({ answer }) => {
        if (!answer) return null;

        return (
            <div className="d-flex gap-2 action-buttons">
                <button
                    className={`btn btn-link p-0 ${answer?.like === true ? 'text-primary' : 'text-dark'}`}
                    onClick={() => handleLikeButton(answer, true)}
                    title="Mi piace"
                >
                    <i className="fa-regular fa-thumbs-up"></i>
                </button>
                <button
                    className={`btn btn-link p-0 ${answer?.like === false ? 'text-danger' : 'text-dark'}`}
                    onClick={() => handleLikeButton(answer, false)}
                    title="Non mi piace"
                >
                    <i className="fa-regular fa-thumbs-down"></i>
                </button>
                <button className="btn btn-link text-dark p-0" onClick={() => handleRegenerateButton(answer)} title="Rigenera">
                    <i className="fa-solid fa-rotate"></i>
                </button>
                {/* dropdown share */}
                <div className="dropdown">
                    <button className="btn btn-link text-dark p-0" data-bs-toggle="dropdown" title="Condividi">
                        <i className="fa-solid fa-share-nodes"></i>
                    </button>
                    <div className="dropdown-menu dropdown-menu-end">
                        <button className="dropdown-item" onClick={() => handleShareButton(answer, 'download')}>
                            <i className="fa-solid fa-download me-2"></i>Download in DOCX
                        </button>
                        <button className="dropdown-item" onClick={() => handleShareButton(answer, 'export')}>
                            <i className="fa-solid fa-file-export me-2"></i>Esporta in Documenti
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Handle like/dislike actions
    const handleLikeButton = async (answer, likeValue) => {
        let value_to_set = likeValue;

        if (answer.like === likeValue) {
            value_to_set = null;
        }

        const result = (await api.sendMessage("dms/setAnswerLikeValue", {
            user: currentUser(),
            body: {
                answerId: answer.id,
                valueToSet: value_to_set
            }
        }))?.inserted?.[0];

        if (result) {
            const updatedHistory = history.map(item =>
                item.id === result.Id ? { ...item, like: result.Like } : item
            );

            setHistory(updatedHistory);
        }
    }

    // Handle regenerate answer
    const handleRegenerateButton = async (answer) => {
        setAnalyzing(true);
        window.$("#answer-actions").hide();
        window.$("#current-answer-div").html("");

        if (toAddInTheHistory) {
            setHistory([toAddInTheHistory, ...history]);
            setToAddInTheHistory(null);
        }

        show_system_message({
            timeOut: 0,
            message: `
            <div class="text-center">
                <i class="fa fa-2x fa-hourglass-start"></i>
            </div>`
        });

        const message = {
            type: "ai_query",
            query: answer.question + " [Rigenerazione]",
            id: window.client?.id,
            file_id: id,
            tutor: "AI_PLAN",
        };

        const result = await api.sendMessage("dms/sendChatMessage", {
            user: currentUser(),
            body: {
                message
            }
        });
    }

    // Handle share/download/export actions
    const handleShareButton = async (answer, type) => {
        switch (type) {
            case 'download':
                withReactContent(Swal).fire({
                    title: "Conferma download",
                    icon: "question",
                    text: "Vuoi scaricare questa risposta come documento?",
                    showConfirmButton: true,
                    showCancelButton: true,
                    confirmButtonText: "Download",
                    cancelButtonText: "Annulla",
                    confirmButtonColor: "#3085d6",
                }).then(async (result) => {
                    if (result.isConfirmed) {
                        try {
                            withReactContent(Swal).fire({
                                title: "Download in corso",
                                text: "Attendere prego...",
                                allowOutsideClick: false,
                                allowEscapeKey: false,
                                showConfirmButton: false,
                                willOpen: () => {
                                    Swal.showLoading();
                                }
                            });

                            const response = await fetch(`${process.env.REACT_APP_API_URL}/dms/downloadAnswer`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': currentUser().token
                                },
                                body: JSON.stringify({
                                    question: answer.question,
                                    answer: answer.answer
                                })
                            });

                            if (!response.ok) {
                                throw new Error(`HTTP error! status: ${response.status}`);
                            }

                            const blob = await response.blob();
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', `${answer.question.substring(0, 30)}.docx`);
                            document.body.appendChild(link);
                            link.click();
                            link.parentNode.removeChild(link);

                            withReactContent(Swal).fire({
                                title: "Download completato",
                                icon: "success",
                                text: "Il documento è stato scaricato con successo",
                                confirmButtonText: "Chiudi"
                            });
                        } catch (error) {
                            console.error('Download error:', error);
                            withReactContent(Swal).fire({
                                title: "Errore",
                                icon: "error",
                                text: `Si è verificato un errore durante il download: ${error.message}`,
                                confirmButtonText: "Chiudi"
                            });
                        }
                    }
                });
                break;
            case 'export':
                withReactContent(Swal).fire({
                    title: "Conferma esportazione",
                    icon: "question",
                    text: "Vuoi esportare questa risposta nei documenti?",
                    showConfirmButton: true,
                    showCancelButton: true,
                    confirmButtonText: "Esporta",
                    cancelButtonText: "Annulla",
                    confirmButtonColor: "#3085d6",
                }).then(async (result) => {
                    if (result.isConfirmed) {
                        try {
                            withReactContent(Swal).fire({
                                title: "Esportazione in corso",
                                text: "Attendere prego...",
                                allowOutsideClick: false,
                                allowEscapeKey: false,
                                showConfirmButton: false,
                                willOpen: () => {
                                    Swal.showLoading();
                                }
                            });

                            const result = await api.sendMessage("dms/exportAnswer", {
                                user: currentUser(),
                                body: {
                                    file_id: id,
                                    question: answer.question,
                                    answer: answer.answer,
                                }
                            });

                            if (!result.success) {
                                throw new Error(result.message || "Errore durante l'esportazione");
                            }

                            if (result?.file?.Id) {
                                withReactContent(Swal).fire({
                                    title: "Export completato",
                                    icon: "success",
                                    text: "Puoi trovare il file nella sezione dms/Documents/exports. Vuoi modificare il documento ora?",
                                    showConfirmButton: true,
                                    showCancelButton: true,
                                    confirmButtonText: "Modifica documento",
                                    cancelButtonText: "No, grazie",
                                    confirmButtonColor: "#3085d6",
                                }).then((editResult) => {
                                    if (editResult.isConfirmed) {
                                        window.location.href = `/dms/edit/${result.file.Id}`;
                                    }
                                });
                            } else {
                                withReactContent(Swal).fire({
                                    title: "Export completato",
                                    icon: "success",
                                    text: "Puoi trovare il file nella sezione dms/Documents/exports",
                                    confirmButtonText: "Chiudi"
                                });
                            }
                        } catch (error) {
                            withReactContent(Swal).fire({
                                title: "Errore",
                                icon: "error",
                                text: "Si è verificato un errore durante l'esportazione",
                                confirmButtonText: "Chiudi"
                            });
                        }
                    }
                });
                break;
        }
    }

    useEffect(() => {
        // Set up breadcrumb
        const path = [
            { link: "/", name: "Home" },
            { link: "/dms", name: "DMS" },
            { link: `/invoice/analyze/${id}`, name: "Invoice Analyzer" }
        ];

        breadcrumb.set(path);

        // Initialize menu and load file if ID exists
        (async () => {
            menu.write({ user: currentUser() });

            if (id) {
                 // Retrieve file info
                const fileInfo = await getFile(id);
                if (fileInfo?.file) {
                    setDocument(fileInfo.file);

                    // Start AI chat session
                    await startChat();
                } else {
                    withReactContent(Swal).fire({
                        title: "Errore",
                        icon: "warning",
                        text: "File non trovato",
                        showConfirmButton: true,
                        confirmButtonText: "Chiudi"
                    }).then(() => {
                        window.location.href = "/dms";
                    });
                }
            }
        })();
    }, [id, currentUser]);

    return (
        <>
            <div className="page-titles">
                <ol className="breadcrumb">
                      {/* Breadcrumb will be populated by the system */}
                </ol>
            </div>
            <div className="container-fluid">
                <div className="row">
                    <div className="col-xl-12">
                        <div className="card">
                            <div className="card-header">
                                <h4 className="card-title">Invoice Analyzer</h4>
                                {document && <p className="mb-0">{document.Name}</p>}
                            </div>
                            <div className="card-body">
                                {analyzing ? (
                                    <div className="text-center mb-4">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Analyzing...</span>
                                        </div>
                                        <p className="mt-2">Analyzing invoice, please wait...</p>
                                    </div>
                                ) : null}

                                {!analyzing && !answerHtml && !toAddInTheHistory && (
                                    <button
                                        className="btn btn-primary mb-4"
                                        onClick={analyzeInvoice}
                                    >
                                        Analyze Invoice
                                    </button>
                                )}

                                {/* Display AI answer */}
                                <div id="current-answer-div" dangerouslySetInnerHTML={{ __html: answerHtml }}></div>

                                {chartComponent && getChart()}

                                {/* Chart container */}
                                <div id="chart-container"></div>

                                {/* Action buttons */}
                                <div id="answer-actions" style={{ display: toAddInTheHistory ? 'block' : 'none' }} className="mt-3">
                                    <ActionButtons answer={toAddInTheHistory} />
                                </div>

                                {/* Previous answers */}
                                {history.length > 0 && (
                                    <div className="mt-4">
                                        <h5>Previous Analyses</h5>
                                        {history.map((item, index) => (
                                            <div key={index} className="card mt-3">
                                                <div className="card-body p-0">
                                                    <div className="table-responsive active-projects task-table">
                                                        <div className="tbl-caption">
                                                            <div>
                                                                <h4 className="heading mb-0">{item.question}</h4>
                                                            </div>
                                                            <div className="basic-form">
                                                                <div className="mb-3 text-small" dangerouslySetInnerHTML={{ __html: item.answer }}></div>
                                                                <ActionButtons answer={item} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export { InvoiceAnalyzerScreen };
